import React from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { insuranceSelector } from '../../../model';
import { useSelector } from 'react-redux';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import moment from 'moment';
import { DateFormats } from 'constants/common';

interface Props {
  onRemove: () => void;
  onSendToLender?: () => void;
}

export const UploadedFileRow = ({ onRemove, onSendToLender }: Props) => {
  const classes = useStyles();

  const insuranceCertificate = useSelector(insuranceSelector.insuranceCertificate)!;
  const insuranceVerificationTask = useSelector(insuranceSelector.insuranceVerificationTask)!;
  const selectedSubmission = useSelector(insuranceSelector.selectedSubmission)!;

  if (!insuranceCertificate) {
    return null;
  }

  const uploadedBy = insuranceVerificationTask?.completionData?.name || insuranceCertificate.createdBy || '—';

  const uploadedAt = insuranceCertificate.createdDateTime
    ? moment(insuranceCertificate.createdDateTime).format(DateFormats.TimeWithShortDate)
    : '—';

  return (
    <Box className={classes.root}>
      <Typography className={classes.title}>Certificate of Insurance:</Typography>

      <Box className={classes.row}>
        <Box className={classes.filePill}>
          <PictureAsPdfOutlinedIcon className={classes.pdfIcon} />

          <Typography className={classes.fileName} noWrap>
            {insuranceCertificate.docName}
          </Typography>

          {/* <Typography className={classes.fileSize} noWrap>
            {insuranceCertificate.fileSize || '—'}
          </Typography> */}

          <IconButton onClick={onRemove} size="small" className={classes.trashBtn}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>

        <Tooltip
          title={
            selectedSubmission && insuranceCertificate
              ? 'Send Certificate of Insurance to Lender'
              : 'Select a submission below to send COI to Lender'
          }
          placement="top"
          arrow
        >
          <span>
            <Button
              variant="contained"
              disableElevation
              className={classes.sendBtn}
              onClick={onSendToLender}
              disabled={!selectedSubmission || !insuranceCertificate}
            >
              SEND TO LENDER
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Box className={classes.metaRow}>
        <Typography className={classes.metaText} noWrap>
          Uploaded by: <span className={classes.metaName}>{uploadedBy}</span>
        </Typography>

        <Typography className={classes.metaText} noWrap>
          {uploadedAt}
        </Typography>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  root: {
    width: '100%',
  },

  title: {
    fontSize: 14,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 10,
  },

  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },

  filePill: {
    flex: 1,
    minWidth: 0,
    height: 44,
    padding: '0 12px',
    backgroundColor: '#EEF0FA',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },

  pdfIcon: {
    color: '#9FA8DA',
    fontSize: 22,
    flexShrink: 0,
  },

  fileName: {
    fontSize: 14,
    color: '#111827',
    fontWeight: 500,
    flex: 1,
    minWidth: 0,
  },

  fileSize: {
    fontSize: 14,
    color: '#6b7280',
    fontWeight: 500,
    marginRight: 2,
    flexShrink: 0,
  },

  trashBtn: {
    color: '#ef4444',
    flexShrink: 0,
  },

  sendBtn: {
    height: 44,
    borderRadius: 6,
    padding: '0 18px',
    fontSize: 13,
    fontWeight: 700,
    textTransform: 'none',
    backgroundColor: '#1D4ED8',
    whiteSpace: 'nowrap',
  },

  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
    gap: 16,
  },

  metaText: {
    fontSize: 14,
    color: '#6b7280',
    fontWeight: 500,
  },

  metaName: {
    color: '#6b7280',
    fontWeight: 500,
  },
});
